import { useCallback } from "react";
import { Button } from "@design-system/Button/Button";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { BreakableText } from "@design-system/BreakableText/BreakableText";
import { useTaskDetail } from "@api/task.api";
import { TaskStatus } from "@common/types/task";
import classNames from "classnames";
import { CloseIcon, SpinnerIcon } from "@icons";
import {
    BlockedByTaskIcon,
    TaskCancelledIcon,
    TaskDoneIcon,
} from "@icons/taskStatuses";
import styles from "../TaskDetail.module.scss";

const NO_ACCESS_TITLE = "No access";

export interface TaskBlockingListItemProps {
    taskId: string;
    onNavigate: (taskId: string) => void;
    onRemove?: (taskId: string) => void;
    displayMode?: "default" | "blockedBy";
}

export const TaskBlockingListItem = ({
    taskId,
    onNavigate,
    onRemove,
    displayMode = "default",
}: Readonly<TaskBlockingListItemProps>) => {
    const {
        data: task,
        isLoading,
        isError,
    } = useTaskDetail(taskId, {
        relatedTask: true,
    });
    const isBlockedByMode = displayMode === "blockedBy";
    const hasNoAccess = task === null;

    const handleNavigate = useCallback(
        () => onNavigate(taskId),
        [onNavigate, taskId],
    );
    const handleRemove = useCallback(
        () => onRemove?.(taskId),
        [onRemove, taskId],
    );
    const isTerminatedStatus =
        task?.status === TaskStatus.DONE ||
        task?.status === TaskStatus.CANCELLED;

    const getLeadingIcon = () => {
        if (task?.status === TaskStatus.DONE) {
            return <TaskDoneIcon className="icon-color-revert" />;
        }

        if (task?.status === TaskStatus.CANCELLED) {
            return <TaskCancelledIcon className="icon-color-revert" />;
        }

        if (isBlockedByMode) {
            return <BlockedByTaskIcon className="icon-color-revert" />;
        }

        return null;
    };

    if (isError || hasNoAccess) {
        return (
            <div className={styles.blockingTaskButtonsContainer}>
                <Button
                    className={classNames(styles.blockingTaskLink, {
                        [styles.blockedByTaskLink]: isBlockedByMode,
                    })}
                    variant="primary"
                    size="md"
                    disabled
                >
                    <BreakableText
                        text={NO_ACCESS_TITLE}
                        className={styles.inaccessibleTaskTitle}
                        copyId={taskId}
                        copyLabel="Task"
                    />
                </Button>
            </div>
        );
    }

    return (
        <div className={styles.blockingTaskButtonsContainer}>
            <Button
                className={classNames(styles.blockingTaskLink, {
                    [styles.blockedByTaskLink]: isBlockedByMode,
                })}
                variant="primary"
                size="md"
                onClick={handleNavigate}
                leftIcon={
                    isLoading ? undefined : (getLeadingIcon() ?? undefined)
                }
            >
                {isLoading ? (
                    <SpinnerIcon />
                ) : (
                    <BreakableText
                        text={task?.title}
                        className={classNames({
                            [styles.terminatedTaskTitle]: isTerminatedStatus,
                        })}
                        copyId={taskId}
                        copyLabel="Task"
                    />
                )}
            </Button>
            {onRemove && (
                <Tooltip content="Remove">
                    <Button
                        className={styles.blockingTaskCloseButton}
                        variant="primary"
                        size="md"
                        iconOnly
                        onClick={handleRemove}
                        aria-label={`Remove ${task?.title ?? "task"}`}
                    >
                        <CloseIcon />
                    </Button>
                </Tooltip>
            )}
        </div>
    );
};
